import React, { useState, useEffect, useRef } from "react";
import { deliveryAPI, logisticsAPI, formatError } from "../utils/api";
import { useKitchen } from "../contexts/KitchenContext";
import { CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw, Loader, Check } from "lucide-react";

const STATUS_CONFIG = {
  pending:       { label: "Pending",       color: "text-[#8A7E73]",  bg: "bg-[#F4F3F0]",   icon: Clock,         border: "border-[#E5E1DB]" },
  delivered:     { label: "Delivered",     color: "text-[#637C64]",  bg: "bg-[#EDF2EE]",   icon: CheckCircle,   border: "border-[#637C64]/30" },
  partial:       { label: "Partial",       color: "text-[#D98032]",  bg: "bg-[#FDF4EB]",   icon: AlertTriangle, border: "border-[#D98032]/30" },
  not_delivered: { label: "Not Delivered", color: "text-[#C2624E]",  bg: "bg-[#FBEAE7]",   icon: XCircle,       border: "border-[#C2624E]/30" },
};

export default function DeliveryTracking() {
  const { selectedKitchen } = useKitchen();
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [deliveries, setDeliveries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [pendingUpdates, setPendingUpdates] = useState({});

  useEffect(() => {
    logisticsAPI.getRoutes().then(r =>
      setRoutes((r.data || []).filter(r => !r.route_number?.startsWith("TEST")))
    );
  }, []);

  useEffect(() => {
    if (selectedRoute && date) { fetchDeliveries(); }
    if (date) fetchSummary();
  }, [selectedRoute, date, selectedKitchen]);

  const fetchDeliveries = async () => {
    setLoading(true);
    try {
      // Try to init route deliveries first (pre-populate)
      try {
        await deliveryAPI.initRoute({ route_id: selectedRoute, date, kitchen_id: selectedKitchen || undefined });
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
      const r = await deliveryAPI.getStatus({ date, route_id: selectedRoute, kitchen_id: selectedKitchen || undefined });
      setDeliveries(r.data || []);
      setPendingUpdates({});
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const fetchSummary = async () => {
    try {
      const r = await deliveryAPI.getSummary({ date, kitchen_id: selectedKitchen || undefined });
      setSummary(r.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
  };

  const setStatus = (schoolName, status) => {
    setPendingUpdates(prev => ({
      ...prev,
      [schoolName]: { ...(prev[schoolName] || {}), status, delivery_time: new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) }
    }));
  };

  const saveAll = async () => {
    if (Object.keys(pendingUpdates).length === 0) return;
    setSaving(true);
    const updates = Object.entries(pendingUpdates).map(([schoolName, data]) => {
      const existing = deliveries.find(d => d.school_name === schoolName);
      return { school_name: schoolName, route_id: selectedRoute, route_number: routes.find(r => r.id === selectedRoute)?.route_number || "", date, kitchen_id: selectedKitchen || null, ...data };
    });
    try {
      await deliveryAPI.bulkUpdate({ route_id: selectedRoute, date, kitchen_id: selectedKitchen || null, deliveries: updates });
      setSuccess(`${updates.length} deliveries updated!`);
      setTimeout(() => setSuccess(""), 2000);
      fetchDeliveries(); fetchSummary();
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setSaving(false);
  };

  const getEffectiveStatus = (schoolName) => {
    if (pendingUpdates[schoolName]) return pendingUpdates[schoolName].status;
    const d = deliveries.find(d => d.school_name === schoolName);
    return d?.status || "pending";
  };

  const currentRoute = routes.find(r => r.id === selectedRoute);
  const schools = currentRoute?.schools || [];
  const pendingCount = Object.keys(pendingUpdates).length;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="delivery-tracking">
      <div>
        <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Delivery Tracking</h1>
        <p className="text-sm text-[#8A7E73] mt-0.5">Track food delivery status to each school per route</p>
      </div>
      <a href="/delivery-kiosk" target="_blank" rel="noopener noreferrer"
        className="bg-[#637C64] text-white rounded-lg px-4 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4e6350]"
        data-testid="open-delivery-kiosk-btn">
        <CheckCircle size={15} /> Open Tablet Kiosk Mode
      </a>

      {success && <div className="bg-[#EDF2EE] border border-[#637C64]/30 rounded-lg px-4 py-2.5 text-sm text-[#637C64] flex items-center gap-2"><Check size={15}/>{success}</div>}

      {/* Daily summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Total", value: summary.total, color: "text-[#3A322C]" },
            { label: "Delivered", value: summary.delivered, color: "text-[#637C64]" },
            { label: "Partial", value: summary.partial, color: "text-[#D98032]" },
            { label: "Not Delivered", value: summary.not_delivered, color: "text-[#C2624E]" },
            { label: "% Complete", value: `${summary.completion_pct}%`, color: "text-[#E8956D]" },
          ].map(s => (
            <div key={s.label} className="bg-white border border-[#E5E1DB] rounded-xl p-3 text-center">
              <p className={`text-xl font-extrabold font-[Manrope] ${s.color}`}>{s.value}</p>
              <p className="text-xs text-[#8A7E73] mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className="flex gap-3 flex-wrap items-end">
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" />
        </div>
        <div>
          <label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Route *</label>
          <select value={selectedRoute} onChange={e => setSelectedRoute(e.target.value)}
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]">
            <option value="">Select Route</option>
            {routes.map(r => <option key={r.id} value={r.id}>{r.route_number} Route ({r.schools?.length || 0} schools)</option>)}
          </select>
        </div>
        {pendingCount > 0 && (
          <button onClick={saveAll} disabled={saving}
            className="bg-[#637C64] text-white rounded-lg px-5 py-2.5 font-bold text-sm flex items-center gap-2 hover:bg-[#4e6350] disabled:opacity-60">
            {saving ? <Loader size={15} className="animate-spin" /> : <Check size={15} />}
            Save {pendingCount} Update(s)
          </button>
        )}
      </div>

      {!selectedRoute ? (
        <div className="text-center py-16 text-[#8A7E73]"><CheckCircle size={48} className="mx-auto mb-3 opacity-20" /><p>Select a route to track deliveries</p></div>
      ) : loading ? (
        <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="px-5 py-3 bg-[#F4F3F0] border-b border-[#E5E1DB] flex items-center justify-between">
            <h3 className="font-bold text-[#3A322C]">{currentRoute?.route_number} Route — {schools.length} schools</h3>
            <div className="flex gap-2">
              <button onClick={() => { const u = {}; schools.forEach(s => { u[s.name] = { status: "delivered", delivery_time: new Date().toLocaleTimeString("en-IN", {hour:"2-digit",minute:"2-digit"}) }; }); setPendingUpdates(u); }}
                className="text-xs bg-[#EDF2EE] text-[#637C64] px-3 py-1.5 rounded-lg font-bold hover:bg-[#637C64] hover:text-white transition-colors">
                Mark All Delivered
              </button>
            </div>
          </div>
          <div className="divide-y divide-[#F4F3F0] max-h-[60vh] overflow-y-auto">
            {schools.map(school => {
              const status = getEffectiveStatus(school.name);
              const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
              const isPending = !!pendingUpdates[school.name];
              return (
                <div key={school.name} className={`px-4 py-3 flex items-center justify-between ${isPending ? "bg-[#E8956D]/5" : ""}`}>
                  <div className="flex-1">
                    <p className={`text-sm font-medium ${isPending ? "font-bold text-[#E8956D]" : "text-[#3A322C]"}`}>{school.name}</p>
                    <p className="text-xs text-[#8A7E73]">{school.address}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {["delivered", "partial", "not_delivered"].map(s => {
                      const sc = STATUS_CONFIG[s];
                      const Icon = sc.icon;
                      return (
                        <button key={s} onClick={() => setStatus(school.name, s)}
                          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg font-bold border transition-all ${status === s ? `${sc.bg} ${sc.color} ${sc.border} border-2` : "border-[#E5E1DB] text-[#8A7E73] hover:border-[#E8956D]/50"}`}>
                          <Icon size={12} />{sc.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
